<script setup lang="ts">
import { ref } from "vue";
import CompanyInformation from "./welcome/CompanyInformation.vue";
import ItemCategoriesListView from "../features/inventory/itemmanagement/itemcategories/ItemCategoriesListView.vue";
import ItemGroupsListView from "../features/inventory/itemmanagement/itemgroups/ItemGroupsListView.vue";
import ItemTypesListView from "../features/inventory/itemmanagement/itemtypes/ItemTypesListView.vue";
import ItemsListView from "../features/inventory/itemmanagement/items/ItemsListView.vue";
import PriceListsListView from "../features/finance/masterdata/pricelists/PriceListsListView.vue";
import CreditTermsListView from "../features/finance/masterdata/creditterms/CreditTermsListView.vue";
import SalesChannelsListView from "../features/revenue/masterdata/saleschannels/SalesChannelsListView.vue";
import Settings from "./welcome/Settings.vue";
import Completed from "./welcome/Completed.vue";
import CurrencyListView from "../features/finance/masterdata/currency/CurrencyListView.vue";
import TaxDefinitionsListView from "../features/finance/masterdata/taxdefinitions/TaxDefinitionsListView.vue";
import { tokenManager } from "../api/utils/tokenManager";
import { api } from "../api/Api";
import WarehousesListView from "../features/system/company/warehouses/WarehousesListView.vue";
import OutletsListView from "../features/system/company/outlets/OutletsListView.vue";
import IconComponent from "../components/ui/common/IconComponent.vue";

interface Step {
  name: string;
  caption: string;
  description: string;
  completed: boolean;
}

const steps: Step[] = [
  {
    name: "company",
    caption: "Company Info",
    description: "Uplaod the Logo and update the Company Information",
    completed: false,
  },
  {
    name: "sales-channel",
    caption: "Sales Channels",
    description: "Create default Sales Channel, for every sale a sales channel is compulsory",
    completed: false,
  },
  {
    name: "price-list",
    caption: "Price List",
    description: "Create default Price List",
    completed: false,
  },
  {
    name: "payment-term",
    caption: "Payment Terms",
    description: "Create Payment Terms",
    completed: false,
  },
  {
    name: "currency",
    caption: "Currency",
    description: "Create Base Currency",
    completed: false,
  },
  {
    name: "tax",
    caption: "Tax Definitions",
    description: "Create Tax Definitions",
    completed: false,
  },
  {
    name: "warehouse",
    caption: "Warehouse",
    description: "Create default warehouse, for each outlet a warehouse is required",
    completed: false,
  },
  {
    name: "outlet",
    caption: "Outlet",
    description: "Create first outlet",
    completed: false,
  },
  {
    name: "item_category",
    caption: "Item category",
    description: "Create Item Categories, It helps to categories items",
    completed: false,
  },
  {
    name: "item_group",
    caption: "Item Group",
    description: "Create Item Groups, It helps grouping the items",
    completed: false,
  },
  {
    name: "item_type",
    caption: "Item Type",
    description: "Item type manages charasterics of Items, this way creating and manaing Items will be easy",
    completed: false,
  },
  {
    name: "item",
    caption: "Items",
    description: "Create some items ",
    completed: false,
  },
  {
    name: "general",
    caption: "General Settings",
    description: "Configure General Settings ",
    completed: false,
  },
  {
    name: "numbering",
    caption: "Number Sequence",
    description: "Configure Numbering Sequnce",
    completed: false,
  },
  {
    name: "accounting",
    caption: "Accounting",
    description: "Configure Accounting Basics ",
    completed: false,
  },  
];

const currentStep = ref<Step>(steps[0])
const isCompleted = ref(false)

const completeStep = (name: string) => {
    let s = steps.find((e) => e.name == name)
    if (s) {
        s.completed = true;
        const stepIndex = steps.indexOf(s);
        if (stepIndex + 1 == steps.length) {
            isCompleted.value = true
            handleCompleted()
        } else {
            currentStep.value = steps[stepIndex+1]
        }
    }    
}


const handleCompleted = async () => {
    const params = {
    'uid': tokenManager.getCompany()?.uid
  }
  const response = await api.post(
    "system/security/users/welcome", params, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    skipLoading: true,
  }  
  );
  if (response.success == true) {
    window.location.href = '/dashboard'
  }
};

const checkStep = (step_name: string) => {
    //TODO: find a way to check if there is more records 
    completeStep(step_name)
}

</script>

<template>
<div
    class="rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 h-[2000px]"
  >
    <div class="p-6 grid grid-cols-3" v-if="!isCompleted">
      <div>
        <div class="text-left">
          <h2 class="text-2xl font-semibold mb-4">Welcome to Faseyha POS</h2>
          <p class="mb-6">Let's get started with setting up your account.</p>
        </div>
      </div>
      <div class="flex items-center space-x-4 col-span-2 justify-between " >
        <template v-for="(step, index) in steps">
        <div class="flex items-center" v-if="currentStep && step.name == currentStep.name">
          <span            
            :class="step.completed ? 'bg-green-500' : (step.name == currentStep.name ? 'bg-red-500' : 'bg-blue-500')"
            class="h-8 w-8 text-white rounded-full flex items-center justify-center relative"
          >
            <svg
              v-if="step.completed"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              class="h-6 w-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span v-else>{{ index + 1 }}</span>
            <span v-if="step.name == currentStep.name" class="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full animate-ping"></span>
          </span>
          <span class="ml-2">{{step.caption}}</span>
        </div>
    </template>
    <div>
      <button
      @click="() => handleCompleted()"
        class="px-2 md:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-red-100 dark:bg-red-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center"
      >
        <IconComponent name="LogOut" />
        <span class="ml-2 uppercase tracking-wide">SKIP GUIDE</span>
      </button>
    </div>
      </div>
      <!-- Steps -->
      <div>
        <ul class="space-y-4 flex-1">
          <li
            v-for="(step, index) in steps"
            :key="index"
            class="flex items-start"
          >
            <span
              class="flex-shrink-0 h-6 w-6 flex items-center justify-center"
              :class="
            step.completed
              ? 'bg-green-500 text-white rounded-full'
              : (step.name == currentStep.name ? 'bg-red-500 text-white rounded-full' : 'bg-blue-500 text-white rounded-full')
              "
            >
              <span
            v-if="step.completed"
            class="flex-shrink-0 h-6 w-6 bg-green-500 rounded-full text-white flex items-center justify-center"
              >
            <svg
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              class="h-4 w-4"
            >
              <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
              />
            </svg>
              </span>
              <span v-else>{{ index + 1 }}</span>
              <span v-if="step.name == currentStep.name" class="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full animate-ping"></span>
            </span>
            <div class="ml-3">
              <h3 :class="step.name == currentStep.name ? 'text-lg font-medium text-white' : 'text-lg font-medium text-gray-600'">{{ step.caption }}</h3>
              <p :class="step.name == currentStep.name ? 'text-sm text-white' : 'text-sm text-gray-500 dark:text-gray-800'">
            {{ step.description }}
              </p>
            </div>
          </li>
        </ul>
      </div>
      <div class="col-span-2">
        <CompanyInformation @save="completeStep('company')" v-if="currentStep.name == 'company'"/>
        <WarehousesListView @next="checkStep('warehouse')" v-if="currentStep.name == 'warehouse'" :show-next-button="true" :show-quick-tour="false"/>
        <OutletsListView @next="checkStep('outlet')" v-if="currentStep.name == 'outlet'" :show-next-button="true" :show-quick-tour="false"/>
        <ItemCategoriesListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('item_category')" v-if="currentStep.name == 'item_category'"/>
        <ItemGroupsListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('item_group')" v-if="currentStep.name == 'item_group'"/>
        <ItemTypesListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('item_type')" v-if="currentStep.name == 'item_type'"/>
        <ItemsListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('item')" v-if="currentStep.name == 'item'"/>
        <SalesChannelsListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('sales-channel')" v-if="currentStep.name == 'sales-channel'"/>
        <PriceListsListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('price-list')" v-if="currentStep.name == 'price-list'"/>
        <CreditTermsListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('payment-term')" v-if="currentStep.name == 'payment-term'"/>
        <CurrencyListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('currency')" v-if="currentStep.name == 'currency'"/>
        <TaxDefinitionsListView :show-quick-tour="false" :show-next-button="true" @next="checkStep('tax')" v-if="currentStep.name == 'tax'"/>
        <Settings section = 'general' @next="checkStep('general')" v-if="currentStep.name == 'general'"/>
        <Settings section = 'numbering' @next="checkStep('numbering')" v-if="currentStep.name == 'numbering'"/>
        <Settings section = 'accounting' @next="checkStep('accounting')" v-if="currentStep.name == 'accounting'"/>
      </div>      
    </div>
    <div>
        <Completed v-if="isCompleted"/>
    </div>
  </div>
</template>

<style scoped>
.bg-white {
  background-color: white;
}
.dark\:bg-gray-900 {
  background-color: #1a202c;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}
.border {
  border-width: 1px;
}
.border-gray-100 {
  border-color: #f7fafc;
}
.dark\:border-gray-800 {
  border-color: #2d3748;
}
.p-6 {
  padding: 1.5rem;
}
.text-2xl {
  font-size: 1.5rem;
}
.font-semibold {
  font-weight: 600;
}
.mb-4 {
  margin-bottom: 1rem;
}
.mb-6 {
  margin-bottom: 1.5rem;
}
.space-y-4 > :not([hidden]) ~ :not([hidden]) {
  --tw-space-y-reverse: 0;
  margin-top: calc(1rem * calc(1 - var(--tw-space-y-reverse)));
  margin-bottom: calc(1rem * var(--tw-space-y-reverse));
}
.flex {
  display: flex;
}
.items-start {
  align-items: flex-start;
}
.flex-shrink-0 {
  flex-shrink: 0;
}
.h-6 {
  height: 1.5rem;
}
.w-6 {
  width: 1.5rem;
}
.text-green-500 {
  color: #48bb78;
}
.ml-3 {
  margin-left: 0.75rem;
}
.text-lg {
  font-size: 1.125rem;
}
.font-medium {
  font-weight: 500;
}
.text-sm {
  font-size: 0.875rem;
}
.text-gray-500 {
  color: #a0aec0;
}
.dark\:text-gray-400 {
  color: #cbd5e0;
}
</style>
